import { Box, Button, Card, CardContent, CircularProgress, IconButton, Typography } from '@material-ui/core'
import { Delete as DeleteIcon, Image as ImageIcon, CloudUpload as UploadIcon } from '@material-ui/icons'
import React, { useCallback, useState } from 'react'
import { Spacer, useToasts } from '@vestaboard/installables'

import { useDeleteHomeScreenBannerImage } from '../hooks/useDeleteHomescreenBannerImage'
import { useDropzone } from 'react-dropzone'
import { useGetHomeScreenBannerImageUploadUrl } from '../hooks/useGetHomeScreenBannerImageUploadUrl'

interface IImageUpload {
  bannerId: string
  currentImageUrl?: string
  onImageUploaded?: (imageUrl: string) => void
  onImageDeleted?: () => void
}

export const ImageUpload = (props: IImageUpload) => {
  const [getUploadUrl] = useGetHomeScreenBannerImageUploadUrl()
  const [deleteImage] = useDeleteHomeScreenBannerImage()
  const { addToast } = useToasts()
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const uploadImage = useCallback(
    async (file: File) => {
      setUploading(true)
      try {
        // Get upload URL
        const uploadResult = await getUploadUrl({
          variables: { id: props.bannerId }
        })

        const uploadData = uploadResult.data?.getHomeScreenBannerImageUploadUrl

        if (uploadData?.__typename === 'HomeScreenBannerImageResponseV2') {
          // Upload the file
          const response = await fetch(uploadData.url, {
            method: 'PUT',
            headers: {
              'Content-Type': file.type
            },
            body: file
          })

          if (response.ok) {
            addToast('Image uploaded successfully', {
              autoDismiss: true,
              appearance: 'success'
            })

            // Construct the final image URL (typically the upload URL without query params)
            const imageUrl = uploadData.url.split('?')[0]
            props.onImageUploaded?.(imageUrl)
            setPreviewUrl(null)
          } else {
            throw new Error('Upload failed')
          }
        } else {
          throw new Error(uploadData?.error || 'Failed to get upload URL')
        }
      } catch (error) {
        console.error('Upload error:', error)
        addToast('Failed to upload image', {
          autoDismiss: true,
          appearance: 'error'
        })
      } finally {
        setUploading(false)
      }
    },
    [getUploadUrl, props, addToast]
  )

  const handleDeleteImage = useCallback(async () => {
    setDeleting(true)
    try {
      const result = await deleteImage({
        variables: { id: props.bannerId }
      })

      const deleteResult = result.data?.deleteHomeScreenBannerImage

      if (deleteResult?.__typename === 'HomeScreenBannerV2') {
        addToast('Image deleted successfully', {
          autoDismiss: true,
          appearance: 'success'
        })
        props.onImageDeleted?.()
      } else {
        throw new Error(deleteResult?.error || 'Failed to delete image')
      }
    } catch (error) {
      console.error('Delete error:', error)
      addToast('Failed to delete image', {
        autoDismiss: true,
        appearance: 'error'
      })
    } finally {
      setDeleting(false)
    }
  }, [deleteImage, props, addToast])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      setPreviewUrl(URL.createObjectURL(file))
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png, image/webp, image/gif',
    maxFiles: 1,
    disabled: uploading || deleting
  })

  const handleUpload = () => {
    if (previewUrl) {
      fetch(previewUrl)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'banner-image.jpg', { type: 'image/jpeg' })
          uploadImage(file)
        })
    }
  }

  const hasCurrentImage = props.currentImageUrl
  const hasPreviewImage = previewUrl

  return (
    <Card>
      <CardContent>
        <Typography variant='h6' gutterBottom>
          Banner Image
        </Typography>

        {hasCurrentImage && !hasPreviewImage && (
          <Box style={{ marginBottom: 16 }}>
            <Typography variant='body2' color='textSecondary' gutterBottom>
              Current Image:
            </Typography>
            <Box style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={props.currentImageUrl}
                alt='Current banner'
                style={{
                  maxWidth: 300,
                  maxHeight: 200,
                  objectFit: 'cover',
                  border: '1px solid #ddd',
                  borderRadius: 4
                }}
              />
              <IconButton
                size='small'
                color='secondary'
                disabled={deleting}
                onClick={handleDeleteImage}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(255,255,255,0.9)'
                }}>
                {deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
              </IconButton>
            </Box>
            <Spacer size='medium' />
          </Box>
        )}

        {hasPreviewImage && (
          <Box style={{ marginBottom: 16 }}>
            <Typography variant='body2' color='textSecondary' gutterBottom>
              New Image Preview:
            </Typography>
            <Box style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={previewUrl}
                alt='Preview'
                style={{
                  maxWidth: 300,
                  maxHeight: 200,
                  objectFit: 'cover',
                  border: '1px solid #ddd',
                  borderRadius: 4
                }}
              />
              <IconButton
                size='small'
                onClick={() => setPreviewUrl(null)}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(255,255,255,0.9)'
                }}>
                <DeleteIcon />
              </IconButton>
            </Box>
            <Spacer size='small' />
            <Button
              variant='contained'
              color='primary'
              onClick={handleUpload}
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}>
              {uploading ? 'Uploading...' : 'Upload Image'}
            </Button>
            <Spacer size='medium' />
          </Box>
        )}

        <Box
          {...getRootProps()}
          style={{
            border: `2px dashed ${isDragActive ? '#3f51b5' : '#ccc'}`,
            borderRadius: 8,
            padding: 32,
            textAlign: 'center',
            cursor: uploading || deleting ? 'not-allowed' : 'pointer',
            backgroundColor: isDragActive ? '#f5f5f5' : 'transparent',
            opacity: uploading || deleting ? 0.5 : 1
          }}>
          <input {...getInputProps()} />
          <ImageIcon style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
          <Typography variant='body1' gutterBottom>
            {isDragActive
              ? 'Drop the image here'
              : hasPreviewImage
              ? 'Drag & drop a different image here, or click to select'
              : 'Drag & drop an image here, or click to select'}
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            Supports JPEG, PNG, and WebP formats
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}
